
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import {
  MdAdd,
  MdDelete,
  MdDownload,
  MdFileUpload,
  MdEmail,
} from "react-icons/md";
import { createApi } from "../../../../core/api";
import { MarketingEmailDto } from "../../../../core/api/types";
import * as XLSX from "xlsx";

const api = createApi();

export const MarketingEmailPage: React.FC = () => {
  const { setPageMenus } = useOutletContext<any>();
  const [emails, setEmails] = useState<MarketingEmailDto[]>([]);
  const [loading, setLoading] = useState(true);

  // Create Modal logic
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEmail, setNewEmail] = useState({
    email: "",
    fullName: "",
    description: "",
  });

  // Upload Modal logic
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadEmails = useCallback(() => {
    setLoading(true);
    api.marketingEmail
      .getAll()
      .then((res) => {
        if (res.ok && res.data) {
          const root = res.data as any;
          setEmails(root.data || []);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMenus([
      {
        label: "Marketing",
        items: [
          { label: "Marketing Emails", route: "/dashboard/marketing-emails" },
        ],
      },
    ]);
    loadEmails();
    return () => setPageMenus([]);
  }, [loadEmails, setPageMenus]);

  const handleDelete = async () => {
    if (!deleteId || deleting) return;
    setDeleting(true);
    try {
      const res = await api.marketingEmail.delete(deleteId);
      if (res.ok) {
        setDeleteId(null);
        loadEmails();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    if (!newEmail.email) return;
    try {
      const res = await api.marketingEmail.create(newEmail);
      if (res.ok) {
        setNewEmail({ email: "", fullName: "", description: "" });
        setShowCreateModal(false);
        loadEmails();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    
    const readFile = (file: File): Promise<any[]> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(sheet);
            console.log("Excel Raw JSON:", json); // DEBUG
            resolve(json);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = (err) => reject(err);
        reader.readAsArrayBuffer(file);
      });
    };

    try {
      const json = await readFile(selectedFile);
      
      const getVal = (row: any, keys: string[]) => {
        const rowKeys = Object.keys(row);
        console.log("Checking Row Keys:", rowKeys); // DEBUG
        for (const k of keys) {
          const foundKey = rowKeys.find(rk => rk.trim().toLowerCase() === k.toLowerCase().replace(/\s/g, ""));
          // Also try with spaces removed from row key
          const foundKeyNoSpace = rowKeys.find(rk => rk.trim().toLowerCase().replace(/\s/g, "") === k.toLowerCase().replace(/\s/g, ""));
          
          const finalKey = foundKey || foundKeyNoSpace;
          if (finalKey) {
            console.log(`Found match for '${k}':`, finalKey, "Value:", row[finalKey]); // DEBUG
            return row[finalKey];
          }
        }
        return null;
      };

      const formattedData = json.map((row: any) => {
        const mapped = {
          email: getVal(row, ["Email", "E-mail", "Eposta", "Mail"]),
          fullName: getVal(row, ["FullName", "Full Name", "Ad Soyad", "AdSoyad", "Name"]),
          description: getVal(row, ["Description", "Açıklama", "Aciklama", "Note", "Info"])
        };
        console.log("Mapped Row:", mapped); // DEBUG
        return mapped;
      }).filter(x => x.email);

      console.log("Final Formatted Data to Send:", formattedData); // DEBUG

      if (formattedData.length === 0) {
        alert("Excel dosyasında geçerli e-posta adresi bulunamadı. Lütfen sütun isimlerini kontrol edin.");
        setUploading(false);
        return;
      }

      const res = await api.marketingEmail.bulkCreate(formattedData);
      console.log("API Bulk Response:", res); // DEBUG
      if (res.ok) {
        setShowUploadModal(false);
        setSelectedFile(null);
        loadEmails();
        alert(`${formattedData.length} adet kayıt başarıyla yüklendi.`);
      } else {
        alert("Yükleme sırasında bir hata oluştu: " + (res as any).message);
      }
    } catch (error) {
      console.error("Excel upload failed:", error);
      alert("Dosya işlenirken bir hata oluştu. Lütfen dosya formatını kontrol edin.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const data = [
        ["Email", "FullName", "Description"],
        ["example@mail.com", "John Doe", "Aviation Professional"],
      ];
      
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      
      XLSX.writeFile(wb, "MarketingEmailTemplate.xlsx");
    } catch (error) {
      console.error("Template generation failed", error);
    }
  };

  const columns = [
    { key: "id", title: "ID", type: "number", minWidth: 80, accessor: (r: any) => r.id },
    {
      key: "email",
      title: "Email",
      type: "text",
      minWidth: 200,
      accessor: (r: any) => r.email,
      render: (_: any, v: string) => (
        <Badge
          label={v}
          icon={MdEmail}
          color="#2563EB"
          bg="rgba(37,99,235,.15)"
        />
      ),
    },
    {
      key: "fullName",
      title: "Full Name",
      type: "text",
      minWidth: 150,
      accessor: (r: any) => r.fullName || r.FullName,
    },
    {
      key: "description",
      title: "Description",
      type: "text",
      minWidth: 200,
      accessor: (r: any) => r.description || r.Description,
    },
    {
      key: "actions",
      title: "Actions",
      filterable: false,
      minWidth: 100,
      render: (row: any) => (
        <GroupButton
          items={[
            {
              key: "delete",
              icon: MdDelete,
              color: "#DC2626",
              onClick: () => setDeleteId(row.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div style={STYLING.wrap}>
      <div style={STYLING.header}>
        <div>
          <div style={STYLING.title}>Marketing Emails</div>
          <div style={STYLING.subTitle}>Manage marketing email list</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            variant="ghost"
            icon={MdDownload}
            onClick={handleDownloadTemplate}
          >
            Template
          </Button>
          <Button
            variant="default"
            icon={MdFileUpload}
            onClick={() => setShowUploadModal(true)}
          >
            Upload Excel
          </Button>
          <Button icon={MdAdd} onClick={() => setShowCreateModal(true)}>
            Add New
          </Button>
        </div>
      </div>

      <Table
        tableKey="marketingEmail"
        loading={loading}
        columns={columns}
        data={emails}
        features={{ sortable: true, filterable: true, pagination: true }}
      />

      {/* Create Modal */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <div style={STYLING.modalTitle}>Add Marketing Email</div>
        <div style={STYLING.form}>
          <input
            style={STYLING.input}
            placeholder="Email *"
            value={newEmail.email}
            onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
          />
          <input
            style={STYLING.input}
            placeholder="Full Name"
            value={newEmail.fullName}
            onChange={(e) =>
              setNewEmail({ ...newEmail, fullName: e.target.value })
            }
          />
          <textarea
            style={{ ...STYLING.input, height: 80, padding: 12 }}
            placeholder="Description"
            value={newEmail.description}
            onChange={(e) =>
              setNewEmail({ ...newEmail, description: e.target.value })
            }
          />
        </div>
        <div style={STYLING.actions}>
          <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate}>Save</Button>
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal open={showUploadModal} onClose={() => setShowUploadModal(false)}>
        <div style={STYLING.modalTitle}>Upload Excel</div>
        <p style={{ fontSize: 13, color: "#64748B" }}>
          Upload an Excel file with the following columns: Email, FullName, Description.
        </p>
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={(e) =>
            setSelectedFile(e.target.files ? e.target.files[0] : null)
          }
        />
        <div style={STYLING.actions}>
          <Button variant="ghost" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          <Button loading={uploading} onClick={handleUpload}>
            Upload
          </Button>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
        <div style={STYLING.modalTitle}>Delete Entry</div>
        <div style={{ marginBottom: 20 }}>
          Are you sure you want to delete this marketing email entry?
        </div>
        <div style={STYLING.actions}>
          <Button variant="ghost" onClick={() => setDeleteId(null)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleting} onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const STYLING: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 0, padding: 20, background: "#F8FAFC" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 700 },
  subTitle: { fontSize: 13, color: "#64748B" },
  modalTitle: { fontSize: 18, fontWeight: 800, marginBottom: 14 },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
};

